import API from '../config/api';
import React, { useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { toast } from 'react-toastify';

import { Container, Row, Col, Card, Badge, Button, Form } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

/* Neutral fallback shown when a product has no image or it fails to load. */
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      <rect width="64" height="64" rx="12" fill="#f3ede1"/>
      <text x="32" y="42" font-size="28" text-anchor="middle" fill="#c2410c">🛍</text>
    </svg>`
  );

const REASONS = [
  { value: 'Damaged Product', icon: '💥', hint: 'Arrived broken or dented' },
  { value: 'Wrong Product', icon: '🔀', hint: 'Not what I ordered' },
  { value: 'Product Not Working', icon: '⚡', hint: 'Faulty or dead on arrival' },
  { value: 'Other', icon: '📝', hint: 'Tell us what went wrong' }
];

const MAX_PHOTOS = 5;

function ApplyRefundPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem('userInfo') || 'null'),
    []
  );

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [refunds, setRefunds] = useState([]);

  /* Form state */
  const [itemIndex, setItemIndex] = useState(0);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!userInfo?._id) {
      navigate('/login');
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, refundsRes] = await Promise.all([
        axios.get(`${API}/orders/${userInfo._id}`),
        axios.get(`${API}/refunds/user/${userInfo._id}`)
      ]);

      setOrders(ordersRes.data || []);
      setRefunds(refundsRes.data || []);
    } catch (error) {
      console.log(error);
      toast.error('Could not load your orders');
    } finally {
      setLoading(false);
    }
  };

  /* Object URLs for the selected photos, cleaned up when they change. */
  const previews = useMemo(
    () => images.map((file) => URL.createObjectURL(file)),
    [images]
  );

  useEffect(() => {
    return () => previews.forEach((url) => URL.revokeObjectURL(url));
  }, [previews]);

  const hasRefund = (id) =>
    refunds.some((refund) => refund.order?.toString() === id?.toString());

  /* Delivered orders the customer hasn't already raised a refund for */
  const eligibleOrders = orders.filter(
    (order) => order.status === 'Delivered' && !hasRefund(order._id)
  );

  const order = orderId
    ? orders.find((o) => o._id?.toString() === orderId?.toString())
    : null;

  const addPhotos = (fileList) => {
    const picked = Array.from(fileList || []);

    const room = MAX_PHOTOS - images.length;

    if (room <= 0) {
      toast.info(`You can attach up to ${MAX_PHOTOS} photos`);
      return;
    }

    if (picked.length > room) {
      toast.info(`Only ${room} more photo${room > 1 ? 's' : ''} can be added`);
    }

    setImages((prev) => [...prev, ...picked.slice(0, room)]);
  };

  const removePhoto = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const submitRefund = async (e) => {
    e.preventDefault();

    if (!reason) {
      toast.error('Please select a reason');
      return;
    }

    if (reason === 'Other' && !description.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    const item = order.orderItems?.[itemIndex];

    if (!item) {
      toast.error('Please choose the product you want refunded');
      return;
    }

    try {
      setSubmitting(true);

      /* Upload evidence photos first (if any), then attach their URLs. */
      let imageUrls = [];

      if (images.length > 0) {
        const formData = new FormData();

        images.forEach((file) => formData.append('images', file));

        const { data } = await axios.post(
          `${API}/upload/multiple`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        imageUrls = data.images || [];
      }

      await axios.post(`${API}/refunds`, {
        user: order.userId,

        userName: order.customerName,

        email: userInfo?.email || '',

        order: order._id,

        orderNumber: `#${order._id.slice(-6)}`,

        product: item.productId,

        productName: item.name,

        reason,

        description,

        images: imageUrls
      });

      toast.success('Refund Request Submitted');

      navigate('/my-refunds');
    } catch (error) {
      console.log(error);

      toast.error('Failed to submit refund');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="arf-wrap">
          <Container>
            <div className="arf-skel arf-skel-hero mb-4" />
            <Row className="g-4">
              <Col lg={7}>
                <div className="arf-skel arf-skel-form" />
              </Col>
              <Col lg={5}>
                <div className="arf-skel arf-skel-side" />
              </Col>
            </Row>
          </Container>
        </div>

        <style>{applyRefundStyles}</style>
      </>
    );
  }

  /* ---- Order picker: no order in the URL ---- */
  if (!orderId) {
    return (
      <>
        <CustomNavbar />

        <div className="arf-wrap">
          <Container>
            <Hero
              title="↩ Apply for a Refund"
              subtitle="Pick the delivered order you'd like refunded"
            />

            {eligibleOrders.length === 0 ? (
              <Card className="border-0 shadow-sm text-center py-5 arf-empty">
                <Card.Body>
                  <div className="arf-empty-icon">📦</div>
                  <h5 className="fw-bold mt-3">No Orders Eligible for Refund</h5>
                  <p className="text-muted">
                    Refunds can be requested on delivered orders that don't
                    already have a request.
                  </p>
                  <Link to="/orders">
                    <Button className="arf-btn-primary fw-semibold">
                      View My Orders
                    </Button>
                  </Link>
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-4">
                {eligibleOrders.map((o) => (
                  <Col xl={4} lg={6} md={6} xs={12} key={o._id}>
                    <Card className="border-0 shadow-sm h-100 arf-pick-card">
                      <Card.Body className="d-flex flex-column p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="arf-order-no">
                            #{o._id.slice(-6)}
                          </span>
                          <Badge bg="success" className="rounded-pill">
                            Delivered
                          </Badge>
                        </div>

                        <div className="d-flex flex-column gap-2 mb-3">
                          {(o.orderItems || []).slice(0, 3).map((item, i) => (
                            <div className="d-flex align-items-center gap-2" key={i}>
                              <img
                                src={item.image || PLACEHOLDER_IMG}
                                alt={item.name}
                                className="arf-thumb-sm"
                                onError={(ev) => {
                                  ev.target.src = PLACEHOLDER_IMG;
                                }}
                              />
                              <span className="arf-item-name">{item.name}</span>
                            </div>
                          ))}
                          {(o.orderItems || []).length > 3 && (
                            <small className="text-muted">
                              +{o.orderItems.length - 3} more item
                              {o.orderItems.length - 3 > 1 ? 's' : ''}
                            </small>
                          )}
                        </div>

                        <small className="text-muted mb-3">
                          📅 {new Date(o.createdAt).toLocaleDateString()}
                        </small>

                        <Link
                          to={`/apply-refund/${o._id}`}
                          className="mt-auto d-grid"
                        >
                          <Button className="arf-btn-primary fw-semibold">
                            ↩ Apply for Refund
                          </Button>
                        </Link>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        </div>

        <style>{applyRefundStyles}</style>
      </>
    );
  }

  /* ---- Guard rails for a specific order ---- */
  const blocker = !order
    ? {
        icon: '🔍',
        title: 'Order Not Found',
        text: "We couldn't find this order in your account."
      }
    : order.status !== 'Delivered'
      ? {
          icon: '🚚',
          title: 'Order Not Delivered Yet',
          text: 'Refunds can only be requested once the order has been delivered.'
        }
      : hasRefund(order._id)
        ? {
            icon: '✅',
            title: 'Refund Already Requested',
            text: 'You have already raised a refund request for this order.'
          }
        : null;

  if (blocker) {
    return (
      <>
        <CustomNavbar />

        <div className="arf-wrap">
          <Container>
            <Card className="border-0 shadow-sm text-center py-5 arf-empty">
              <Card.Body>
                <div className="arf-empty-icon">{blocker.icon}</div>
                <h5 className="fw-bold mt-3">{blocker.title}</h5>
                <p className="text-muted">{blocker.text}</p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Link to="/orders">
                    <Button variant="outline-secondary" className="fw-semibold">
                      My Orders
                    </Button>
                  </Link>
                  <Link to="/my-refunds">
                    <Button className="arf-btn-primary fw-semibold">
                      My Refunds
                    </Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </div>

        <style>{applyRefundStyles}</style>
      </>
    );
  }

  const items = order.orderItems || [];
  const selectedItem = items[itemIndex];

  return (
    <>
      <CustomNavbar />

      <div className="arf-wrap">
        <Container>
          <Hero
            title="↩ Apply for a Refund"
            subtitle={`Order #${order._id.slice(-6)} · placed ${new Date(
              order.createdAt
            ).toLocaleDateString()}`}
          />

          <Form onSubmit={submitRefund}>
            <Row className="g-4">
              {/* Form */}
              <Col lg={7}>
                <Card className="border-0 shadow-sm arf-card mb-4">
                  <Card.Body className="p-4">
                    <h6 className="arf-section-title">1 · Which product?</h6>

                    <div className="d-flex flex-column gap-2">
                      {items.map((item, i) => (
                        <label
                          key={i}
                          className={`arf-item ${i === itemIndex ? 'is-active' : ''}`}
                        >
                          <input
                            type="radio"
                            name="refund-item"
                            className="arf-item-radio"
                            checked={i === itemIndex}
                            onChange={() => setItemIndex(i)}
                          />
                          <img
                            src={item.image || PLACEHOLDER_IMG}
                            alt={item.name}
                            className="arf-thumb"
                            onError={(ev) => {
                              ev.target.src = PLACEHOLDER_IMG;
                            }}
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold arf-item-name">
                              {item.name}
                            </div>
                            <small className="text-muted">
                              Qty {item.quantity || item.qty || 1} · ₹
                              {item.price}
                            </small>
                          </div>
                          <span className="arf-tick">✓</span>
                        </label>
                      ))}
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm arf-card mb-4">
                  <Card.Body className="p-4">
                    <h6 className="arf-section-title">2 · What went wrong?</h6>

                    <div className="arf-reasons">
                      {REASONS.map((r) => (
                        <label
                          key={r.value}
                          className={`arf-reason ${reason === r.value ? 'is-active' : ''}`}
                        >
                          <input
                            type="radio"
                            name="refund-reason"
                            className="arf-item-radio"
                            checked={reason === r.value}
                            onChange={() => setReason(r.value)}
                          />
                          <span className="arf-reason-icon">{r.icon}</span>
                          <span className="fw-semibold">{r.value}</span>
                          <small className="text-muted">{r.hint}</small>
                        </label>
                      ))}
                    </div>

                    <Form.Group className="mt-4">
                      <Form.Label className="fw-semibold mb-1">
                        Description{' '}
                        {reason === 'Other' ? (
                          <span className="text-danger">*</span>
                        ) : (
                          <span className="text-muted fw-normal">
                            (optional)
                          </span>
                        )}
                      </Form.Label>

                      <Form.Control
                        as="textarea"
                        rows={4}
                        maxLength={500}
                        placeholder="Tell us a little more so we can review faster…"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="arf-input"
                      />

                      <div className="text-end">
                        <small className="text-muted">
                          {description.length}/500
                        </small>
                      </div>
                    </Form.Group>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm arf-card">
                  <Card.Body className="p-4">
                    <h6 className="arf-section-title">
                      3 · Add photos{' '}
                      <span className="text-muted fw-normal">(optional)</span>
                    </h6>

                    <p className="text-muted small">
                      Photos of the issue (e.g. a broken product) help us review
                      your request faster. Up to {MAX_PHOTOS} images.
                    </p>

                    <label className="arf-drop">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="d-none"
                        onChange={(e) => {
                          addPhotos(e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <span className="arf-drop-icon">📷</span>
                      <span className="fw-semibold">Click to upload photos</span>
                      <small className="text-muted">
                        {images.length}/{MAX_PHOTOS} added
                      </small>
                    </label>

                    {previews.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {previews.map((url, i) => (
                          <div key={url} className="arf-photo-wrap">
                            <img
                              src={url}
                              alt={`evidence ${i + 1}`}
                              className="arf-photo"
                            />
                            <button
                              type="button"
                              className="arf-photo-remove"
                              aria-label="Remove photo"
                              onClick={() => removePhoto(i)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Summary */}
              <Col lg={5}>
                <div className="arf-sticky">
                  <Card className="border-0 shadow-sm arf-card mb-4">
                    <Card.Body className="p-4">
                      <h6 className="arf-section-title">Request Summary</h6>

                      <div className="arf-summary-row">
                        <span className="arf-label">Order</span>
                        <span className="fw-semibold">
                          #{order._id.slice(-6)}
                        </span>
                      </div>

                      <div className="arf-summary-row">
                        <span className="arf-label">Product</span>
                        <span className="fw-semibold text-end">
                          {selectedItem?.name || '—'}
                        </span>
                      </div>

                      <div className="arf-summary-row">
                        <span className="arf-label">Reason</span>
                        <span className="fw-semibold text-end">
                          {reason || 'Not selected'}
                        </span>
                      </div>

                      <div className="arf-summary-row">
                        <span className="arf-label">Photos</span>
                        <span className="fw-semibold">{images.length}</span>
                      </div>

                      <div className="d-grid gap-2 mt-4">
                        <Button
                          type="submit"
                          disabled={submitting}
                          className="arf-btn-primary fw-semibold py-2"
                        >
                          {submitting ? 'Submitting…' : '↩ Submit Refund Request'}
                        </Button>

                        <Button
                          variant="outline-secondary"
                          className="fw-semibold"
                          disabled={submitting}
                          onClick={() => navigate('/orders')}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card className="border-0 shadow-sm arf-card arf-policy">
                    <Card.Body className="p-4">
                      <h6 className="arf-section-title">How it works</h6>

                      <ol className="arf-policy-list">
                        <li>We review your request within 24–48 hours.</li>
                        <li>
                          You'll get an email at every status change, and can
                          track it under{' '}
                          <Link to="/my-refunds">My Refunds</Link>.
                        </li>
                        <li>
                          Once approved, the amount is refunded to your original
                          payment method within 5–7 working days.
                        </li>
                      </ol>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>

      <style>{applyRefundStyles}</style>
    </>
  );
}

function Hero({ title, subtitle }) {
  return (
    <Card className="border-0 shadow-lg mb-4 arf-hero">
      <span className="arf-hero-blob arf-hero-blob-1" />
      <span className="arf-hero-blob arf-hero-blob-2" />

      <Card.Body className="position-relative p-4 p-md-5">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h1 className="fw-bold mb-1">{title}</h1>
            <p className="mb-0 arf-hero-text">{subtitle}</p>
          </div>

          <Link to="/my-refunds">
            <Button className="arf-hero-btn fw-semibold">💰 My Refunds</Button>
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
}

const applyRefundStyles = `
  .arf-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 48px) 0;
  }

  /* Hero */
  .arf-hero {
    border-radius: 22px;
    overflow: hidden;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #7c2d12 60%, #ea580c);
    position: relative;
  }
  .arf-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    pointer-events: none;
  }
  .arf-hero-blob-1 {
    width: 200px; height: 200px;
    background: #f59e0b;
    top: -80px; left: 20%;
    animation: arfFloat 9s ease-in-out infinite;
  }
  .arf-hero-blob-2 {
    width: 180px; height: 180px;
    background: #22c55e;
    bottom: -90px; right: 8%;
    opacity: 0.3;
    animation: arfFloat 11s ease-in-out infinite reverse;
  }
  .arf-hero-text { opacity: 0.85; }
  .arf-hero-btn {
    background: rgba(255,255,255,0.16) !important;
    border: 1px solid rgba(255,255,255,0.3) !important;
    border-radius: 30px;
    padding: 10px 20px;
    color: #fff !important;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .arf-hero-btn:hover {
    background: rgba(255,255,255,0.28) !important;
    transform: translateY(-2px);
  }

  /* Cards */
  .arf-card { border-radius: 18px; }
  .arf-section-title {
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 16px;
  }
  .arf-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #94a3b8;
  }
  .arf-input {
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 12px 14px;
  }
  .arf-input:focus {
    border-color: #ea580c;
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.15);
  }
  .arf-btn-primary {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .arf-btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #1e293b, #9a3412);
    box-shadow: 0 10px 20px rgba(15,23,42,0.25);
  }

  /* Product picker */
  .arf-item-radio {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
  .arf-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 14px;
    padding: 12px 14px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  .arf-item:hover { transform: translateY(-2px); }
  .arf-item.is-active {
    border-color: #ea580c;
    background: #fff7ed;
  }
  .arf-thumb {
    width: 52px; height: 52px;
    object-fit: cover;
    border-radius: 10px;
    background: #f8fafc;
    flex: 0 0 auto;
  }
  .arf-thumb-sm {
    width: 34px; height: 34px;
    object-fit: cover;
    border-radius: 8px;
    background: #f8fafc;
    flex: 0 0 auto;
  }
  .arf-item-name {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 0.92rem;
  }
  .arf-tick {
    width: 24px; height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    background: #e5e7eb;
    color: transparent;
    flex: 0 0 auto;
    transition: background 0.2s ease, color 0.2s ease;
  }
  .arf-item.is-active .arf-tick,
  .arf-reason.is-active .arf-tick {
    background: #ea580c;
    color: #fff;
  }

  /* Reason picker */
  .arf-reasons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .arf-reason {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 2px solid #e5e7eb;
    border-radius: 14px;
    padding: 14px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  .arf-reason:hover { transform: translateY(-2px); }
  .arf-reason.is-active {
    border-color: #ea580c;
    background: #fff7ed;
  }
  .arf-reason-icon { font-size: 1.4rem; line-height: 1; }
  @media (max-width: 575.98px) {
    .arf-reasons { grid-template-columns: 1fr; }
  }

  /* Photo upload */
  .arf-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    border: 2px dashed #fed7aa;
    border-radius: 14px;
    padding: 24px;
    cursor: pointer;
    background: #fffbf5;
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  .arf-drop:hover {
    border-color: #ea580c;
    background: #fff7ed;
  }
  .arf-drop-icon { font-size: 1.8rem; }
  .arf-photo-wrap { position: relative; }
  .arf-photo {
    width: 82px; height: 82px;
    object-fit: cover;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
  }
  .arf-photo-remove {
    position: absolute;
    top: -6px; right: -6px;
    width: 22px; height: 22px;
    border-radius: 50%;
    border: none;
    background: #ef4444;
    color: #fff;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
  }
  .arf-photo-remove:hover { background: #dc2626; }

  /* Summary */
  .arf-sticky { position: sticky; top: 90px; }
  @media (max-width: 991.98px) {
    .arf-sticky { position: static; }
  }
  .arf-summary-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px dashed #e5e7eb;
  }
  .arf-summary-row:last-of-type { border-bottom: none; }
  .arf-policy { background: #f8fafc; }
  .arf-policy-list {
    margin: 0;
    padding-left: 18px;
    color: #475569;
    font-size: 0.9rem;
  }
  .arf-policy-list li { margin-bottom: 8px; }
  .arf-policy-list li:last-child { margin-bottom: 0; }

  /* Order picker cards */
  .arf-pick-card {
    border-radius: 16px;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .arf-pick-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 22px 40px rgba(194, 65, 12, 0.18) !important;
  }
  .arf-order-no {
    font-weight: 800;
    color: #64748b;
    font-size: 0.85rem;
    letter-spacing: 0.3px;
  }

  /* Empty / blocked states */
  .arf-empty { border-radius: 18px; }
  .arf-empty-icon {
    font-size: 3.4rem;
    animation: arfFloat 4s ease-in-out infinite;
  }

  @keyframes arfFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }

  /* Loading skeletons */
  .arf-skel {
    background: linear-gradient(90deg, rgba(148,163,184,0.14) 25%, rgba(255,255,255,0.6) 37%, rgba(148,163,184,0.14) 63%);
    background-size: 400% 100%;
    animation: arfShimmer 1.4s ease infinite;
    border-radius: 18px;
  }
  .arf-skel-hero { height: 160px; }
  .arf-skel-form { height: 460px; }
  .arf-skel-side { height: 300px; }
  @keyframes arfShimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .arf-hero-blob, .arf-empty-icon, .arf-skel { animation: none; }
  }
`;

export default ApplyRefundPage;
